import { useMemo } from 'react';

import { StaticCurrencySpan, StaticNumericSpan } from '@myn/mui';

import {
  BUILDER_ROW_DEPTH,
  BUILDER_ROW_TYPES,
  DECIMAL_LIMIT,
  EMPTY_VALUE,
} from '../../../../../../constants/campaignBuilder.js';
import { useCampaignDataSelector } from '../../../../../../hooks/useGetCampaignData.js';
import type { PredictiveBudgetFieldProps } from '../../../../../../types/predictivePlanning.js';

type CellValue = PredictiveBudgetFieldProps['value'];

export const useGetPredictiveValue = ({
  column,
  row,
  value,
}: PredictiveBudgetFieldProps) => {
  const rowCurrency = row.original.currencyValue;
  const isTotalRow = row.original.type === BUILDER_ROW_TYPES.TOTAL;
  const isCurrencyField = !!column.isCurrency && !!rowCurrency;
  const isEmptyValue = (!value && value !== 0) || value === EMPTY_VALUE;
  const decimalLimit = column.decimals ?? DECIMAL_LIMIT.BUDGET;
  const isJourneyCurrencyMode = row.original.isCampaignCurrencyMode;
  const { data: currency } = useCampaignDataSelector('currency');

  const isMediaRowAndLocalCurrencyMode =
    !isJourneyCurrencyMode && row.depth === BUILDER_ROW_DEPTH.MEDIA_ROW;

  const getCurrencyModeValue = (id: string, cellValue: CellValue) => {
    if (isMediaRowAndLocalCurrencyMode) {
      const cellValueInLocalCurrency = row.original[
        `${id}InLocalCurrency` as keyof typeof row.original
      ] as CellValue;

      return cellValueInLocalCurrency;
    }
    return cellValue;
  };

  const { numberValue, currentCurrency } = useMemo(() => {
    const newValue = getCurrencyModeValue(column.id, value);

    return {
      numberValue: newValue ?? 0,
      currentCurrency: isJourneyCurrencyMode ? currency?.value : rowCurrency,
    };
  }, [isJourneyCurrencyMode, rowCurrency, currency, column.id, value]);

  const formattedField = useMemo(() => {
    if (isEmptyValue) {
      return null;
    }

    if (isCurrencyField && currentCurrency) {
      return (
        <StaticCurrencySpan
          number={numberValue}
          currency={currentCurrency}
          options={{
            minDecimals: decimalLimit,
            maxDecimals: decimalLimit,
            min: 0,
          }}
        />
      );
    }

    return (
      <StaticNumericSpan
        number={numberValue}
        options={{
          minDecimals: decimalLimit,
          maxDecimals: decimalLimit,
          min: 0,
        }}
      />
    );
  }, [decimalLimit, isCurrencyField, isTotalRow, numberValue, currentCurrency]);

  return { predictiveField: formattedField };
};
